import { z } from 'zod';

import { META_CTA_OPTIONS } from './meta/options';

import { HOOK_FRAMEWORK_IDS, type HookFrameworkId } from '@/lib/content-doctrine/ad-copy-doctrine';
import {
  ANTI_CORNY_CHECK_IDS,
  type AdReadinessFinding,
  checkAdReadiness,
  extractAdCopyHook,
} from '@/lib/content-doctrine/ad-readiness';
import {
  checkPaidCopySlop,
  paidCopySlopFindingsAsMessages,
} from '@/lib/content-doctrine/paid-copy-slop-gate';
import { HOOK_MECHANISMS, type HookMechanism } from '@/lib/creative-labeling/taxonomy';

const HashSchema = z.string().regex(/^[a-f0-9]{64}$/i);
const UuidSchema = z.string().uuid();
const EvidenceSchema = z.object({ id: z.string().min(1), text: z.string().trim().min(8) }).strict();

const HttpUrlSchema = z
  .string()
  .url()
  .refine((value) => {
    try {
      const protocol = new URL(value).protocol;
      return protocol === 'http:' || protocol === 'https:';
    } catch {
      return false;
    }
  }, 'Use a complete http(s) URL.');

export const PaidAdCopyGroundingSchema = z
  .object({
    productOrOffer: z.string().trim().min(1),
    destinationUrl: HttpUrlSchema,
    audienceSummary: z.string().trim().min(1),
    brandSnapshotHash: HashSchema,
    brandMode: z.enum(['brand', 'neutral']),
    differentiatingEvidence: z.array(EvidenceSchema).min(1),
  })
  .strict();
export type PaidAdCopyGrounding = z.infer<typeof PaidAdCopyGroundingSchema>;

export type PaidAdCopyGroundingField =
  | 'productOrOffer'
  | 'destinationUrl'
  | 'audienceSummary'
  | 'brandSnapshotHash'
  | 'differentiatingEvidence';

export type PaidAdCopyGroundingIssue = Readonly<{
  field: PaidAdCopyGroundingField;
  message: string;
}>;

export type PaidAdCopyGroundingInput = Readonly<{
  productOrOffer?: string | null;
  destinationUrl?: string | null;
  audienceSummary?: string | null;
  brandSnapshotHash?: string | null;
  brandMode?: 'brand' | 'neutral' | null;
  differentiatingEvidence?: readonly { id: string; text: string }[];
}>;

export function paidAdCopyGroundingIssues(
  input: PaidAdCopyGroundingInput,
): readonly PaidAdCopyGroundingIssue[] {
  const candidate = {
    productOrOffer: input.productOrOffer ?? '',
    destinationUrl: input.destinationUrl ?? '',
    audienceSummary: input.audienceSummary ?? '',
    brandSnapshotHash: input.brandSnapshotHash ?? '',
    brandMode: input.brandMode ?? 'neutral',
    differentiatingEvidence: input.differentiatingEvidence ?? [],
  };
  const result = PaidAdCopyGroundingSchema.safeParse(candidate);
  if (result.success) return [];

  const byField = new Map<PaidAdCopyGroundingField, PaidAdCopyGroundingIssue>();
  result.error.issues.forEach((issue) => {
    const field = issue.path[0];
    if (
      field !== 'productOrOffer' &&
      field !== 'destinationUrl' &&
      field !== 'audienceSummary' &&
      field !== 'brandSnapshotHash' &&
      field !== 'differentiatingEvidence'
    ) {
      return;
    }
    if (byField.has(field)) return;
    const messages: Record<PaidAdCopyGroundingField, string> = {
      productOrOffer: 'Choose the product or offer this ad promotes.',
      destinationUrl: 'Add a complete destination URL.',
      audienceSummary: 'Describe or select the audience this ad should address.',
      brandSnapshotHash: 'Resolve a Brand Kit or choose explicit neutral mode.',
      differentiatingEvidence: 'Add one product fact, offer detail, or evidence point.',
    };
    byField.set(field, { field, message: messages[field] });
  });
  return [...byField.values()];
}

export const PaidAdCopyActionSchema = z.enum([
  'draft',
  'suggest',
  'shorten',
  'make_specific',
  'adapt',
]);
export type PaidAdCopyAction = z.infer<typeof PaidAdCopyActionSchema>;

export const PaidAdCopyTargetSchema = z.discriminatedUnion('provider', [
  z
    .object({
      provider: z.literal('meta-ads'),
      localAdId: UuidSchema,
      field: z.enum(['all', 'primaryText', 'headline', 'description']),
    })
    .strict(),
  z
    .object({
      provider: z.literal('google-ads'),
      localAdGroupId: UuidSchema,
      localRsaId: UuidSchema,
      field: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('all') }).strict(),
        z
          .object({
            kind: z.enum(['headline', 'description']),
            localAssetId: UuidSchema,
          })
          .strict(),
      ]),
    })
    .strict(),
]);
export type PaidAdCopyTarget = z.infer<typeof PaidAdCopyTargetSchema>;

/**
 * Browser request for one suggestion run. Grounding, current copy, Brand and
 * evidence hashes are deliberately absent: the server resolves all of them
 * from this exact persisted draft revision and target.
 */
export const PaidAdCopyGenerationRequestSchema = z
  .object({
    draftId: UuidSchema,
    expectedRevision: z.number().int().positive(),
    clientRequestId: UuidSchema,
    action: PaidAdCopyActionSchema,
    target: PaidAdCopyTargetSchema,
    /**
     * "Iterate this ad" (PLAN-iterate-this-ad-2026-09-03.md Unit 5) — the delivered
     * `paid_ad_execution_commands.id` this suggestion should react to, when the request originated
     * from `PaidAdExecutionReceipt.tsx`'s "Iterate this ad" button (`onIterate`, carried onto the
     * URL as `iterateFromCommandId`, `PaidAdCreationDialog.tsx`'s `parsePaidAdSourceSeed`). Optional
     * and absent for every ordinary generation request — the server resolves the prior ad's
     * eligibility itself from this id alone (`copy-suggestions-core.ts`'s `resolvePriorAdGround`);
     * the browser sends only the id, never a client-computed verdict.
     */
    iterateFromCommandId: UuidSchema.optional(),
  })
  .strict();
export type PaidAdCopyGenerationRequest = z.infer<typeof PaidAdCopyGenerationRequestSchema>;

export const PaidAdCopySuggestionApplicationSchema = z
  .object({
    suggestionId: UuidSchema,
    candidateId: UuidSchema,
  })
  .strict();
export type PaidAdCopySuggestionApplication = z.infer<typeof PaidAdCopySuggestionApplicationSchema>;

const MetaCtaSchema = z.enum(META_CTA_OPTIONS.map((option) => option.value));

const SuggestionEvidenceSchema = z
  .object({
    evidenceIds: z.array(z.string().min(1)).min(1),
    citationIds: z.array(z.string().min(1)).min(1),
    brandSnapshotHash: HashSchema,
    summary: z.string().trim().min(1).max(240),
  })
  .strict();

/**
 * Nit H (2026-09-03 fix round, closed in the 2026-09-04 re-fix): the judge's `needsReview` flag
 * and its reasons (`generate-grounded-ad-copy.ts`'s `needsReview`/`warnings`) were computed on
 * every generated candidate and then dropped at `copy-suggestions-core.ts`'s `normalizeCandidates`
 * boundary — a marketer applying a candidate never saw that Mora's own anti-corny gate or
 * creative-review judge had a reservation about it. As of the re-fix, the full chain is live:
 * `copy-suggestion-run-contract.ts` carries these same two optional fields on the wire payload,
 * `copy-suggestions-core.ts`'s `normalizeCandidates` sets them for real — per-candidate corny
 * findings for Meta (each of the three candidates scored independently), the whole-outcome
 * `produced.needsReview`/`warnings` applied uniformly for Google RSA (its candidates are asset
 * slices of one engine call, never independently re-scored) — `usePaidAdCopyAssistant.ts`'s
 * `visibleSuggestion()` maps both fields through and runs `sortCopyCandidatesForReview` (below),
 * and `PaidAdCopyAssistant.tsx` renders the reasons on the selected candidate. See
 * `docs/planning/PLAN-iterate-this-ad-2026-09-03.md` Unit 5 for the trace this fix round closed.
 *
 * `.optional()`, still — not every candidate carries a reservation, and an older ledger-replayed
 * payload predates this field. A reader must treat `undefined` as "unknown, not necessarily
 * clean" and never silently coerce it to "clean" — see `needsReviewOrUnknown` below, the one place
 * that coercion decision is made once, not re-decided ad hoc at each call site.
 */
export const CandidateReviewFieldsSchema = {
  needsReview: z.boolean().optional(),
  reviewReasons: z.array(z.string().trim().min(1)).max(20).optional(),
};

const MetaCopyCandidateSchema = z
  .object({
    localCandidateId: z.string().uuid(),
    primaryText: z.string().trim().min(1).max(2_200),
    headline: z.string().trim().min(1).max(255),
    description: z.string().trim().max(255),
    cta: MetaCtaSchema,
    rationale: z.string().trim().min(1).max(240),
    ...CandidateReviewFieldsSchema,
  })
  .strict();

const GoogleRsaCandidateSchema = z
  .object({
    localCandidateId: z.string().uuid(),
    headlines: z.array(z.string().trim().min(1).max(30)).min(3).max(15),
    descriptions: z.array(z.string().trim().min(1).max(90)).min(2).max(4),
    rationale: z.string().trim().min(1).max(240),
    ...CandidateReviewFieldsSchema,
  })
  .strict();

export const PaidAdCopySuggestionSchema = z.discriminatedUnion('provider', [
  z
    .object({
      provider: z.literal('meta-ads'),
      requestHash: HashSchema,
      candidates: z.array(MetaCopyCandidateSchema).min(1).max(3),
      grounding: SuggestionEvidenceSchema,
    })
    .strict(),
  z
    .object({
      provider: z.literal('google-ads'),
      requestHash: HashSchema,
      candidates: z.array(GoogleRsaCandidateSchema).min(1).max(3),
      grounding: SuggestionEvidenceSchema,
    })
    .strict(),
]);
export type PaidAdCopySuggestion = z.infer<typeof PaidAdCopySuggestionSchema>;

/**
 * `candidate.needsReview` is `undefined` when a candidate genuinely carries no reservation, or
 * when it came from a payload persisted before this field existed. Neither is "known clean" —
 * coalescing `undefined` to `false` would tell a marketer Mora's judge signed off on a candidate
 * it never actually reviewed. `null` here means exactly that: unknown, not clean. One function, so
 * a caller like `sortCopyCandidatesForReview` below never has to re-decide the coercion itself.
 */
export function needsReviewOrUnknown(candidate: { needsReview?: boolean }): boolean | null {
  return candidate.needsReview ?? null;
}

/**
 * Nit H (2026-09-03 fix round): "sorted after gate-clean candidates" as a pure, tested function
 * rather than a rendering-time `.sort()` a caller has to re-derive correctly — this repo's own
 * "rules that must hold go in code, not prose" lesson. A STABLE sort (`Array.prototype.toSorted`
 * with a boolean-weighted comparator, never a fresh-index reshuffle): candidates with the same
 * review status keep the order the server ranked them in (`copy-suggestions-core.ts` already sorts
 * Meta candidates by hook score before this runs). `needsReviewOrUnknown === null` (unknown) is
 * treated as gate-clean for ordering purposes — an unknown status is not evidence of a problem, and
 * ranking it alongside genuinely flagged candidates would overstate what Mora actually found.
 */
export function sortCopyCandidatesForReview<T extends { needsReview?: boolean }>(
  candidates: readonly T[],
): T[] {
  const weight = (candidate: T) => (needsReviewOrUnknown(candidate) === true ? 1 : 0);
  return [...candidates]
    .map((candidate, index) => ({ candidate, index }))
    .sort((a, b) => weight(a.candidate) - weight(b.candidate) || a.index - b.index)
    .map(({ candidate }) => candidate);
}

export function shouldAskOnlyDifferentiator(issues: readonly PaidAdCopyGroundingIssue[]): boolean {
  return issues.length === 1 && issues[0]?.field === 'differentiatingEvidence';
}

/**
 * THE POST-GENERATION CRITIQUE — the countable half of the ANTI-CORNY gates
 * `ad-copy-doctrine.ts` describes in prose. This is the second of the two
 * places that doctrine promises will "encode them as checks... not prose",
 * and it deliberately does not re-derive the rules: it runs the exact same
 * `checkAdReadiness` report `ad-readiness.ts` uses for full launch review,
 * against a synthetic ad (real copy, placeholder creative/destination so the
 * unrelated assembly checks never fire), and keeps only the corny findings.
 * One implementation, two call sites — never a second vocabulary.
 */
export interface AdCopyAntiCornyCandidate {
  primaryText: string;
  headline: string;
  description?: string | null;
  cta?: string | null;
  audienceMode?: 'prospecting' | 'warm' | 'existing_customers' | 'advantage_plus' | null;
}

const ANTI_CORNY_CHECK_ID_SET = new Set(ANTI_CORNY_CHECK_IDS);

export function evaluateAdCopyAntiCorny(
  candidate: AdCopyAntiCornyCandidate,
): readonly AdReadinessFinding[] {
  const report = checkAdReadiness({
    primaryText: candidate.primaryText,
    headline: candidate.headline,
    description: candidate.description ?? undefined,
    cta: candidate.cta ?? undefined,
    // Placeholder, non-null values — this call only ever wants the corny
    // findings (filtered below), never the assembly ones ("no creative", "no
    // destination") a real candidate preview has not resolved yet.
    creativeRef: 'anti-corny-critique-only',
    destinationUrl: 'https://placeholder.invalid/anti-corny-critique-only',
    audienceMode: candidate.audienceMode ?? null,
  });
  return report.findings.filter((finding) => ANTI_CORNY_CHECK_ID_SET.has(finding.id));
}

/**
 * THE THIRD CALL SITE of the paid-copy money-moving gate (incident
 * 2026-09-02/03, `content-doctrine/paid-copy-slop-gate.ts`) — the same
 * `checkPaidCopySlop` the server's execution boundary runs, and the same one
 * `validateMetaStandardAd` now runs at the Ad step and Review. Applied here
 * to a GENERATED candidate before it is ever offered: a candidate this gate
 * blocks is `applicable: false` and must not be shown, scored, or ranked —
 * `evaluateAdCopyAntiCorny` above only scores/critiques (a candidate can win
 * on hook quality despite a corny finding), which is the wrong shape for a
 * hard rule with zero exceptions. `messages` reuses
 * `paidCopySlopFindingsAsMessages` verbatim — never a second, hand-authored
 * wording of the same refusal.
 */
export interface AdCopyCandidateSlopInput {
  primaryText: string;
  headline: string;
  description?: string | null;
}

export interface AdCopyCandidateSlopReport {
  applicable: boolean;
  messages: readonly string[];
}

export function evaluateAdCopyCandidateSlop(
  candidate: AdCopyCandidateSlopInput,
): AdCopyCandidateSlopReport {
  const report = checkPaidCopySlop({
    primaryText: candidate.primaryText,
    headline: candidate.headline,
    description: candidate.description ?? null,
  });
  return {
    applicable: !report.blocked,
    messages: paidCopySlopFindingsAsMessages(report.findings),
  };
}

/**
 * The hook mechanism taxonomy from `creative-labeling/taxonomy.ts`, reused
 * rather than forked — `HOOK_MECHANISMS` already exists for exactly this
 * question ("what job does the opening move do?") for finished creatives;
 * this applies the same evidence-or-null discipline to a generated hook
 * before it is ever attached to a creative. Deterministic and regex-only, by
 * the same reasoning `slop.ts` gives for staying regex-only: a real signal a
 * script can catch honestly, not a semantic judgment dressed as one.
 */
export interface HookMechanismLabel {
  value: HookMechanism | null;
  evidence: string | null;
}

const HOOK_MECHANISM_UNIVERSE = new Set<string>(HOOK_MECHANISMS);

function labelMechanism(value: HookMechanism, evidence: string): HookMechanismLabel {
  if (!HOOK_MECHANISM_UNIVERSE.has(value)) return { value: null, evidence: null };
  return { value, evidence };
}

export function classifyHookMechanism(primaryText: string): HookMechanismLabel {
  const hook = extractAdCopyHook(primaryText).trim();
  if (!hook) return { value: null, evidence: null };

  if (/\?\s*$/.test(hook)) {
    return labelMechanism('question', `Ends on a question mark: "${hook}"`);
  }
  if (/^(?:imagine|picture this|it'?s \d{1,2}(?::\d{2})?\s?(?:am|pm))/i.test(hook)) {
    return labelMechanism('story_open', `Opens on a scene: "${hook}"`);
  }
  if (
    /^(?:you'?re|still |running |managing |the (?:solo|shopify|dtc)\b)/i.test(hook) ||
    /\byou'?re\b.{0,40}\?$/i.test(hook)
  ) {
    return labelMechanism('callout', `Names the reader directly: "${hook}"`);
  }
  if (/\b(?:free|% off|save \$|starting at|guarantee(?:d)?)\b/i.test(hook)) {
    return labelMechanism('offer_led', `Leads with the offer terms: "${hook}"`);
  }
  if (/\b(?:vs\.?|instead of|without|unlike|not\b.{0,30}\bbut\b)/i.test(hook)) {
    return labelMechanism(
      'contrast',
      `States a before/after or against-the-grain comparison: "${hook}"`,
    );
  }
  if (/^\d|\bin \d+\s*(?:days?|hours?|minutes?|weeks?)\b/i.test(hook)) {
    return labelMechanism('claim', `Opens on a specific number or timeframe: "${hook}"`);
  }
  return { value: null, evidence: null };
}

/**
 * A DETERMINISTIC 1-10, built from the taxonomy classification plus the
 * anti-corny findings — never an LLM opinion. `creative-labeling/taxonomy.ts`
 * deliberately rejected a 1-10 grade for a finished creative ("a grade of
 * your work reads as judgment"); this is a different object (an unshipped
 * candidate being ranked against its own siblings, not a library asset being
 * judged), and it stays honest by only ever counting things a script can
 * count: a nameable mechanism is a stronger base, every corny finding costs
 * points, a hard fail costs more than a warning.
 */
export function scoreAdCopyHookCandidate(
  mechanism: HookMechanismLabel,
  cornyFindings: readonly AdReadinessFinding[],
): number {
  let score = mechanism.value ? 8 : 5;
  for (const finding of cornyFindings) {
    score -= finding.severity === 'fail' ? 3 : 1;
  }
  return Math.max(1, Math.min(10, score));
}

/** One line a human can read while picking a candidate — never "review this". */
export function buildAdCopyCandidateCritique(
  mechanism: HookMechanismLabel,
  cornyFindings: readonly AdReadinessFinding[],
): string {
  if (cornyFindings.length === 0) {
    return mechanism.value
      ? `Hook reads as ${mechanism.value.replace(/_/g, ' ')}. No anti-corny issues found.`
      : 'No anti-corny issues found, though the hook does not clearly match a named mechanism.';
  }
  const worst = cornyFindings.find((finding) => finding.severity === 'fail') ?? cornyFindings[0];
  return worst.detail;
}

/**
 * True only when a set of Meta copy suggestion candidates each carry a
 * DIFFERENT hook framework — the fix for finding (5), 2026-09-03, made
 * checkable rather than merely hoped for: three candidates that are three
 * rerolls of one framework are three candidates in name only. Also false
 * (never true by omission) when a candidate's framework is unrecognized —
 * every entry must be a real `HOOK_FRAMEWORK_IDS` member.
 */
export function candidatesUseDistinctFrameworks(
  frameworks: readonly (HookFrameworkId | null | undefined)[],
): boolean {
  if (frameworks.length === 0) return false;
  const known = new Set<string>(HOOK_FRAMEWORK_IDS);
  const seen = new Set<HookFrameworkId>();
  for (const framework of frameworks) {
    if (!framework || !known.has(framework) || seen.has(framework)) return false;
    seen.add(framework);
  }
  return true;
}
